import type {
  IReservation,
  TReservationSource,
} from '@/interfaces/IReservation';
import type { IReservationStats } from '@/interfaces/IReservationStats';
import type { IStatsExportColumn } from '@/domain/stats/stats-export';
import { formatTime } from './reservation-time';

export interface IReservationExportRows {
  columns: IStatsExportColumn[];
  rows: Record<string, unknown>[];
}

const sources: TReservationSource[] = [
  'online',
  'staff',
  'phone',
  'walk_in',
  'waitlist',
];

export function reservationStatsExportRows(
  stats: IReservationStats,
  t: (key: string) => string
): IReservationExportRows {
  const columns: IStatsExportColumn[] = [
    { key: 'date', header: t('reservations.form.date'), type: 'date' },
    {
      key: 'reservations',
      header: t('reservations.reports.kpi.bookings'),
      type: 'number',
    },
    {
      key: 'covers',
      header: t('reservations.reports.kpi.covers'),
      type: 'number',
    },
  ];
  for (const source of sources) {
    columns.push({
      key: source,
      header: `${t(`reservations.source.${source}`)} (${t(
        'reservations.reports.kpi.covers'
      )})`,
      type: 'number',
    });
  }
  return {
    columns,
    rows: stats.perDay.map((day) => {
      const row: Record<string, unknown> = {
        date: day.date,
        reservations: day.reservations,
        covers: day.covers,
      };
      for (const source of sources) row[source] = day.bySource[source];
      return row;
    }),
  };
}

export function reservationsAgendaExportRows(
  rows: readonly IReservation[],
  t: (key: string) => string,
  tz: string
): IReservationExportRows {
  return {
    columns: [
      { key: 'date', header: t('reservations.form.date'), type: 'date' },
      { key: 'time', header: t('reservations.form.time'), type: 'string' },
      { key: 'name', header: t('reservations.form.name'), type: 'string' },
      { key: 'phone', header: t('reservations.form.phone'), type: 'string' },
      { key: 'party', header: t('reservations.form.party'), type: 'number' },
      { key: 'status', header: t('status'), type: 'string' },
      { key: 'source', header: t('reservations.form.source'), type: 'string' },
      {
        key: 'tables',
        header: t('reservations.detail.tables'),
        type: 'string',
      },
      {
        key: 'guestNotes',
        header: t('reservations.form.guestNotes'),
        type: 'string',
      },
    ],
    rows: rows.map((row) => ({
      date: row.serviceDate,
      time: formatTime(row.startsAt, tz),
      name: row.guestName ?? '',
      phone: row.guestPhone ?? '',
      party: row.partySize,
      status: t(`reservations.status.${row.status}`),
      source: t(`reservations.source.${row.source}`),
      tables: row.tableNames.join(', '),
      guestNotes: row.notesGuest ?? '',
    })),
  };
}
